import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  vehiclesApi,
  vehicleTypesApi,
  partnersApi,
  peopleApi,
  type Vehicle,
  type VehicleWrite,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Button } from '@/components/Button';
import { Modal, ConfirmModal } from '@/components/Modal';
import { adminNavItems } from '@/pages/navItems';
import { useToast } from '@/components/Feedback';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';
import { ownershipLabel, vehicleStatusLabel } from '@/lib/labels';
import { formatManilaDate } from '@dispatchdash/shared';

const EMPTY_FORM: VehicleWrite & { helperSelect: string } = {
  plateNumber: '',
  vehicleTypeId: '',
  ownership: 'owned',
  partnerId: '',
  status: 'available',
  standingDriverId: '',
  standingHelperIds: [],
  helperSelect: '',
  currentOdometer: 0,
  tankCapacityLitres: '',
  acquisitionCost: '',
  acquisitionDate: '',
  costShape: 'monthly_lease',
  crewSource: 'in_house',
  monthlyCost: '',
  dailyHireCost: '',
  breakEvenTripCount: null,
};

function personLabel(p: { fullName?: string; name?: string }) {
  return p.fullName ?? p.name ?? 'Unknown';
}

export function AdminVehicles() {
  const { data: vehicles, loading, error, reload } = useAsync(() => vehiclesApi.list(), []);
  const { data: vehicleTypes, loading: typesLoading } = useAsync(() => vehicleTypesApi.list(), []);
  const { data: partners, loading: partnersLoading } = useAsync(() => partnersApi.list(), []);
  const { data: crew } = useAsync(() => peopleApi.list({ isCrew: true }), []);

  const activeTypes = useMemo(
    () => (vehicleTypes ?? []).filter((t) => t.status === 'active'),
    [vehicleTypes],
  );

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const { flash, ok, error: showError, clear } = useFlash();
  const toast = useToast();
  const [confirmDelete, setConfirmDelete] = useState<Vehicle | null>(null);

  const openAdd = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      vehicleTypeId: activeTypes[0]?.id ?? '',
    });
    clear();
    setShowForm(true);
  };

  const openEdit = (row: Vehicle) => {
    setEditing(row);
    setForm({
      plateNumber: row.plateNumber ?? row.plate ?? '',
      vehicleTypeId: row.vehicleTypeId,
      ownership: row.ownership,
      partnerId: row.partnerId ?? '',
      status: row.status,
      standingDriverId: row.standingDriverId ?? '',
      standingHelperIds: row.standingHelperIds ?? [],
      helperSelect: '',
      currentOdometer: row.currentOdometer ?? 0,
      tankCapacityLitres: row.tankCapacityLitres ?? '',
      acquisitionCost: row.acquisitionCost ?? '',
      acquisitionDate: row.acquisitionDate
        ? formatManilaDate(row.acquisitionDate)
        : '',
      costShape: row.costShape ?? 'monthly_lease',
      crewSource: row.crewSource ?? 'in_house',
      monthlyCost: row.monthlyCost ?? '',
      dailyHireCost: row.dailyHireCost ?? '',
      breakEvenTripCount: row.breakEvenTripCount ?? null,
    });
    clear();
    setShowForm(true);
  };

  const buildPayload = (): VehicleWrite => ({
    plateNumber: form.plateNumber.trim(),
    vehicleTypeId: form.vehicleTypeId,
    ownership: form.ownership,
    partnerId: form.ownership === 'subcon' ? form.partnerId || null : null,
    status: form.status,
    standingDriverId: form.standingDriverId || null,
    standingHelperIds: form.standingHelperIds ?? [],
    currentOdometer: Number(form.currentOdometer ?? 0),
    tankCapacityLitres: form.tankCapacityLitres || null,
    acquisitionCost:
      form.ownership === 'owned' ? form.acquisitionCost || null : null,
    acquisitionDate:
      form.ownership === 'owned' && form.acquisitionDate
        ? new Date(form.acquisitionDate).toISOString()
        : null,
    costShape: form.ownership === 'subcon' ? form.costShape || null : null,
    crewSource: form.ownership === 'subcon' ? form.crewSource || null : null,
    monthlyCost: form.ownership === 'subcon' ? form.monthlyCost || null : null,
    dailyHireCost: form.ownership === 'subcon' ? form.dailyHireCost || null : null,
    breakEvenTripCount:
      form.ownership === 'subcon' && form.breakEvenTripCount != null
        ? Number(form.breakEvenTripCount)
        : null,
  });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    clear();
    try {
      const payload = buildPayload();
      if (editing) {
        await vehiclesApi.update(editing.id, payload);
      } else {
        await vehiclesApi.create(payload);
      }
      setShowForm(false);
      toast(editing ? `Vehicle ${payload.plateNumber ?? ''} updated` : `Vehicle ${payload.plateNumber ?? ''} added`);
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not save the vehicle.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    try {
      await vehiclesApi.remove(confirmDelete.id);
      setConfirmDelete(null);
      ok('Vehicle deleted');
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not delete the vehicle.'));
      setConfirmDelete(null);
    } finally {
      setSaving(false);
    }
  };

  const addHelper = () => {
    if (!form.helperSelect) return;
    if ((form.standingHelperIds ?? []).includes(form.helperSelect)) return;
    setForm({
      ...form,
      standingHelperIds: [...(form.standingHelperIds ?? []), form.helperSelect],
      helperSelect: '',
    });
  };

  const removeHelper = (id: string) => {
    setForm({
      ...form,
      standingHelperIds: (form.standingHelperIds ?? []).filter((h) => h !== id),
    });
  };

  const crewOptions = crew ?? [];

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header row-between">
          <div>
            <p className="kicker">Fleet</p>
            <h1>Vehicles</h1>
            <p className="text-muted">
              Your trucks and subcontracted units, with their usual driver and helpers
            </p>
          </div>
          <Button onClick={openAdd} disabled={!activeTypes.length}>Add vehicle</Button>
        </div>

        {!typesLoading && !activeTypes.length && (
          <p className="text-warning">
            Configure{' '}
            <Link to="/app/admin/settings?tab=vehicle-types">vehicle types in Settings</Link>{' '}
            before registering fleet units.
          </p>
        )}

        <FlashMessage flash={flash} />

        <DataTable
          searchPlaceholder="Filter vehicles…"
          columns={[
            {
              key: 'plate',
              header: 'Plate',
              value: (r) => r.plateNumber ?? r.plate ?? '',
              render: (r) => r.plateNumber ?? r.plate,
            },
            {
              key: 'type',
              header: 'Type',
              value: (r) =>
                r.vehicleTypeName
                  ? `${r.vehicleTypeCode} · ${r.vehicleTypeName}`
                  : (r.type ?? ''),
              render: (r) =>
                r.vehicleTypeName
                  ? `${r.vehicleTypeCode} · ${r.vehicleTypeName}`
                  : (r.type ?? '—'),
            },
            {
              key: 'ownership',
              header: 'Ownership',
              value: (r) => ownershipLabel(r.ownership),
              render: (r) => ownershipLabel(r.ownership),
            },
            {
              key: 'status',
              header: 'Status',
              value: (r) => vehicleStatusLabel(r.status),
              render: (r) => <StatusBadge status={r.status} size="sm" />,
            },
            {
              key: 'driver',
              header: 'Standing driver',
              value: (r) => r.driverName ?? '',
              render: (r) => r.driverName ?? '—',
            },
            {
              key: 'odometer',
              header: 'Odometer',
              value: (r) => r.currentOdometer ?? null,
              render: (r) =>
                r.currentOdometer != null
                  ? `${r.currentOdometer.toLocaleString('en-PH')} km`
                  : '—',
            },
            {
              key: 'actions',
              header: '',
              sortable: false,
              render: (r) => (
                <div className="row-actions">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}>Delete</Button>
                </div>
              ),
            },
          ]}
          data={vehicles ?? []}
          keyField="id"
          loading={loading}
          loadError={error}
          dense
          emptyMessage="No vehicles registered. Add your fleet to enable dispatch assignments."
        />

        <Modal
          open={showForm}
          onClose={() => setShowForm(false)}
          title={editing ? 'Edit vehicle' : 'Add vehicle'}
          size="lg"
        >
          <form onSubmit={save} className="vehicle-form">
            <div className="grid-2">
              <Input
                label="Plate number"
                value={form.plateNumber}
                onChange={(e) => setForm({ ...form, plateNumber: e.target.value })}
                required
                placeholder="ABC 1234"
              />
              <Select
                label="Vehicle type"
                value={form.vehicleTypeId}
                onChange={(e) => setForm({ ...form, vehicleTypeId: e.target.value })}
                required
              >
                <option value="">Select…</option>
                {activeTypes.map((t) => (
                  <option key={t.id} value={t.id}>{t.code} · {t.name}</option>
                ))}
              </Select>
            </div>

            <div className="grid-2">
              <Select
                label="Ownership"
                value={form.ownership}
                onChange={(e) =>
                  setForm({
                    ...form,
                    ownership: e.target.value as 'owned' | 'subcon',
                  })
                }
              >
                <option value="owned">Company-owned</option>
                <option value="subcon">Subcontracted</option>
              </Select>
              <Select
                label="Availability status"
                value={form.status ?? 'available'}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="available">{vehicleStatusLabel('available')}</option>
                <option value="on_trip">{vehicleStatusLabel('on_trip')}</option>
                <option value="maintenance">{vehicleStatusLabel('maintenance')}</option>
                <option value="unavailable">{vehicleStatusLabel('unavailable')}</option>
              </Select>
            </div>

            {form.ownership === 'subcon' && (
              <>
                <Select
                  label="Subcontractor"
                  value={form.partnerId ?? ''}
                  onChange={(e) => setForm({ ...form, partnerId: e.target.value })}
                  hint="The company that supplies this unit"
                >
                  <option value="">Select subcontractor…</option>
                  {(partners ?? []).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </Select>
                {!partnersLoading && !(partners ?? []).length && (
                  <p className="text-warning" style={{ fontSize: '0.8125rem', marginTop: '-0.5rem' }}>
                    No subcontractors yet.{' '}
                    <Link to="/app/admin/partners">Add a subcontractor</Link> first, then come back to this vehicle.
                  </p>
                )}
                <div className="grid-2">
                  <Select
                    label="Cost shape"
                    value={form.costShape ?? 'monthly_lease'}
                    onChange={(e) => setForm({ ...form, costShape: e.target.value })}
                  >
                    <option value="per_trip_lane_rate">Per-trip lane rate</option>
                    <option value="per_trip_ad_hoc">Per-trip ad hoc</option>
                    <option value="monthly_lease">Monthly lease</option>
                    <option value="daily_hire">Daily hire</option>
                  </Select>
                  <Select
                    label="Crew source"
                    value={form.crewSource ?? 'in_house'}
                    onChange={(e) => setForm({ ...form, crewSource: e.target.value })}
                  >
                    <option value="in_house">In-house</option>
                    <option value="vendor">Vendor</option>
                  </Select>
                </div>
                <div className="grid-2">
                  <Input
                    label="Monthly cost (₱)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.monthlyCost ?? ''}
                    onChange={(e) => setForm({ ...form, monthlyCost: e.target.value })}
                  />
                  <Input
                    label="Daily hire cost (₱)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.dailyHireCost ?? ''}
                    onChange={(e) => setForm({ ...form, dailyHireCost: e.target.value })}
                  />
                </div>
                <Input
                  label="Break-even trip count / month"
                  type="number"
                  min="0"
                  value={form.breakEvenTripCount != null ? String(form.breakEvenTripCount) : ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      breakEvenTripCount: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                  hint="For leased units — used for utilisation alerts"
                />
              </>
            )}

            {form.ownership === 'owned' && (
              <div className="grid-2">
                <Input
                  label="Acquisition cost (₱)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.acquisitionCost ?? ''}
                  onChange={(e) => setForm({ ...form, acquisitionCost: e.target.value })}
                />
                <Input
                  label="Acquisition date"
                  type="date"
                  value={form.acquisitionDate ?? ''}
                  onChange={(e) => setForm({ ...form, acquisitionDate: e.target.value })}
                />
              </div>
            )}

            <div className="grid-2">
              <Input
                label="Current odometer (km)"
                type="number"
                min="0"
                value={String(form.currentOdometer ?? 0)}
                onChange={(e) => setForm({ ...form, currentOdometer: Number(e.target.value) })}
              />
              <Input
                label="Tank capacity (L)"
                type="number"
                step="0.01"
                min="0"
                value={form.tankCapacityLitres ?? ''}
                onChange={(e) => setForm({ ...form, tankCapacityLitres: e.target.value })}
              />
            </div>

            <Select
              label="Standing driver"
              value={form.standingDriverId ?? ''}
              onChange={(e) => setForm({ ...form, standingDriverId: e.target.value })}
              hint="Snapshotted onto trips at confirmation"
            >
              <option value="">None yet</option>
              {crewOptions.map((p) => (
                <option key={p.id} value={p.id}>{personLabel(p)}</option>
              ))}
            </Select>

            <div className="helpers-block">
              <label className="input-label">Standing helpers</label>
              <div className="row">
                <select
                  className="input-field"
                  value={form.helperSelect}
                  onChange={(e) => setForm({ ...form, helperSelect: e.target.value })}
                >
                  <option value="">Add helper…</option>
                  {crewOptions
                    .filter((p) => p.id !== form.standingDriverId)
                    .filter((p) => !(form.standingHelperIds ?? []).includes(p.id))
                    .map((p) => (
                      <option key={p.id} value={p.id}>{personLabel(p)}</option>
                    ))}
                </select>
                <Button type="button" variant="secondary" size="sm" onClick={addHelper}>
                  Add
                </Button>
              </div>
              {(form.standingHelperIds ?? []).length > 0 && (
                <ul className="helper-list">
                  {(form.standingHelperIds ?? []).map((id) => {
                    const person = crewOptions.find((p) => p.id === id);
                    return (
                      <li key={id}>
                        <span>{person ? personLabel(person) : 'Former crew member'}</span>
                        <Button type="button" size="sm" variant="ghost" onClick={() => removeHelper(id)}>
                          Remove
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {showForm && flash?.kind === 'error' && <FlashMessage flash={flash} />}
            <Button type="submit" loading={saving}>
              {editing ? 'Save vehicle' : 'Register vehicle'}
            </Button>
          </form>
        </Modal>

        <ConfirmModal
          open={!!confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={remove}
          title="Delete vehicle"
          message={
            confirmDelete
              ? `Delete plate ${confirmDelete.plateNumber ?? confirmDelete.plate}?`
              : ''
          }
          confirmLabel="Delete"
          danger
          loading={saving}
        />

        <style>{`
          .row-actions { display: flex; gap: 0.25rem; justify-content: flex-end; }
          .vehicle-form .grid-2 {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: var(--space-md);
          }
          @media (max-width: 640px) {
            .vehicle-form .grid-2 { grid-template-columns: 1fr; }
          }
          .helpers-block { margin-bottom: var(--space-md); }
          .helpers-block .input-label {
            display: block;
            font-weight: 500;
            margin-bottom: var(--space-xs);
            font-size: 0.875rem;
          }
          .helpers-block .input-field {
            flex: 1;
            padding: var(--space-sm) var(--space-md);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
          }
          .helper-list {
            list-style: none;
            padding: 0;
            margin: var(--space-sm) 0 0;
          }
          .helper-list li {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: var(--space-xs) 0;
            border-bottom: 1px solid var(--color-border);
            font-size: 0.875rem;
          }
        `}</style>
      </div>
    </Layout>
  );
}
